import { map, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { RequestService } from './request.service';
import { CommonRequestService } from './common-request.service';
import { CommonResponse } from '../models/responses/common-response';

const roo_path = '/hospital-contract';

@Injectable({
  providedIn: 'root',
})
export class HospitalContractService extends CommonRequestService {
  constructor(protected requestService: RequestService) {
    super(requestService, roo_path);
  }

  public onSave(data: {
    file_attachment: File[];
    payload: {
      _id?: string;
      contract_type?: any;
      hospital?: any;
      package_level?: any;
      start_date?: any;
      service_start_date?: any;
      is_contract?: any;

      payment_type?: any;
      quality_assessment?: any;
      report_period?: any;
      report_base?: any;

      bank?: any;
      bank_account?: any;
      bank_holder_name?: any;

      od?: any;
      hospital_level?: any;
      bonus_percentage?: any;
      [key: string]: any;
    };
  }): Observable<any> {
    const formData = new FormData();

    data.file_attachment.forEach((file: File) => {
      formData.append('file_attachment', file);
    });

    if (data.payload._id != null) formData.append('_id', data.payload._id);
    if (data.payload.contract_type != null)
      formData.append('contract_type', data.payload.contract_type);
    if (data.payload.hospital != null)
      formData.append('hospital', data.payload.hospital);
    if (data.payload.package_level != null)
      formData.append('package_level', data.payload.package_level);
    if (data.payload.start_date != null)
      formData.append('start_date', data.payload.start_date);
    if (data.payload.service_start_date !== undefined)
      formData.append(
        'service_start_date',
        data.payload.service_start_date || '',
      );
    if (data.payload.is_contract != null)
      formData.append('is_contract', data.payload.is_contract);

    if (data.payload.payment_type != null)
      formData.append('payment_type', data.payload.payment_type);
    if (data.payload.quality_assessment != null)
      formData.append(
        'quality_assessment',
        data.payload.quality_assessment.toString(),
      );
    if (data.payload.report_period != null)
      formData.append('report_period', data.payload.report_period.toString());
    if (data.payload.report_base != null)
      formData.append('report_base', data.payload.report_base);

    if (data.payload.bank !== undefined)
      formData.append('bank', data.payload.bank || '');
    if (data.payload.bank_account !== undefined)
      formData.append('bank_account', data.payload.bank_account || '');
    if (data.payload.bank_holder_name !== undefined)
      formData.append('bank_holder_name', data.payload.bank_holder_name || '');

    if (data.payload.od != null) formData.append('od', data.payload.od);
    if (data.payload.hospital_level != null)
      formData.append('hospital_level', data.payload.hospital_level);
    if (data.payload.bonus_percentage != null)
      formData.append(
        'bonus_percentage',
        data.payload.bonus_percentage.toString(),
      );

    return this.requestService.postData(`${this.root_path}/save`, {
      data: formData,
      option: {
        is_loading: true,
      },
    });
  }

  public uploadDocumentFile(
    files: FileList,
    type: any,
    _id: string,
  ): Observable<CommonResponse<any>> {
    let data = new FormData();
    for (let i = 0; i < files.length; i++) {
      data.append('file', files[i]);
    }
    data.append('type', type);

    return this.requestService.postData(`${roo_path}/${_id}/attachment`, {
      data: data,
      option: {
        is_loading: true,
      },
    });
  }

  getTemplateCaseBase(
    _id: string,
    loading: boolean = true,
  ): Observable<CommonResponse<any>> {
    return this.requestService.getJSON<any>(
      `${this.root_path}/template-casebase/${_id}`,
      {
        data: {},
        option: {
          is_loading: loading,
        },
      },
    );
  }

  public onSaveCase(idOrData: any, dataOrCasebases?: any): Observable<any> {
    let contractId = '';
    let casebasesList: Array<any> = [];

    if (typeof idOrData === 'string') {
      contractId = idOrData;
      if (Array.isArray(dataOrCasebases)) {
        casebasesList = dataOrCasebases;
      } else if (dataOrCasebases && typeof dataOrCasebases === 'object') {
        casebasesList = dataOrCasebases.casebases || [];
      }
    } else if (idOrData && typeof idOrData === 'object') {
      contractId =
        idOrData._id ||
        idOrData.payload?._id ||
        (typeof dataOrCasebases === 'string' ? dataOrCasebases : '');
      casebasesList =
        idOrData.casebases ||
        idOrData.payload?.casebases ||
        (Array.isArray(dataOrCasebases)
          ? dataOrCasebases
          : dataOrCasebases?.casebases || []);
    }

    const formData = new FormData();

    if (Array.isArray(casebasesList)) {
      casebasesList.forEach((item: any, index: number) => {
        const lumsumpServiceId = item.case?._id ?? item.case;
        if (lumsumpServiceId != null) {
          formData.append(`casebases[${index}][case]`, lumsumpServiceId);
        }
        const visitCategoryId = item.visit_category?._id ?? item.visit_category;
        if (visitCategoryId != null && visitCategoryId !== '') {
          formData.append(
            `casebases[${index}][visit_category]`,
            visitCategoryId.toString(),
          );
        }
        if (item.price != null) {
          formData.append(`casebases[${index}][price]`, item.price.toString());
        }
      });
    }

    return this.requestService.postData(
      `${this.root_path}/${contractId}/casebase`,
      {
        data: formData,
        option: {
          is_loading: true,
        },
      },
    );
  }

  public deleteReason(id: string, reason?: string): Observable<any> {
    return this.requestService.delete_V2(`${this.root_path}/${id}/delete`, {
      data: {
        delete_reason: reason,
      },
      option: {
        is_loading: true,
      },
    });
  }

  public deleteDocumentFile(id: string, loading = true, _id: string) {
    return this.requestService
      .deleteJSON(`${roo_path}/${_id}/attachment/${id}`, {
        option: {
          is_loading: loading,
        },
      })
      .pipe(
        map((response) => {
          const data = Object.assign(response, { data: { _id: id } });
          return data;
        }),
      );
  }

  public suspend(_id: string, data: any): Observable<any> {
    return this.requestService.postJSON(
      `${this.root_path}/${_id}/suspend`,
      {
        data,
        option: {
          is_loading: true,
        },
      },
      true,
    );
  }

  public terminate(_id: string, data: any): Observable<any> {
    return this.requestService.postJSON(
      `${this.root_path}/${_id}/terminate`,
      {
        data,
        option: {
          is_loading: true,
        },
      },
      true,
    );
  }

  public reopen(_id: string): Observable<any> {
    return this.requestService.postJSON(
      `${this.root_path}/${_id}/reopen`,
      {
        data: {},
        option: {
          is_loading: true,
        },
      },
      true,
    );
  }
}
